<script lang="ts">
  import { Users, Layers, SlidersHorizontal, Route as RouteIcon, BarChart3, Globe, Mail, Building2, KeyRound } from 'lucide-svelte';
  import { route } from '$lib/state/route.svelte';
  import { session } from '$lib/state/session.svelte';
  import UsersPage from './UsersPage.svelte';
  import VerticalsPage from './VerticalsPage.svelte';
  import CustomFieldsPage from './CustomFieldsPage.svelte';
  import StatsPage from './StatsPage.svelte';
  import LeadRoutingPage from './LeadRoutingPage.svelte';
  import TrackedDomainsPage from './TrackedDomainsPage.svelte';
  import EmailAccessPage from './EmailAccessPage.svelte';
  import CompaniesPage from '../entities/CompaniesPage.svelte';
  import HappyCodeSettings from './HappyCodeSettings.svelte';
  import ApiKeysPage from './ApiKeysPage.svelte';

  // "Users & Access" (with its inline login-audit feed) is the pixel-perfect
  // screen from the redesign brief and the default tab. Stats/Verticals/
  // Custom fields/Lead routing are pre-existing real settings screens with
  // no prototype counterpart — kept, under a generic "Settings" grouping
  // label, rather than deleting working admin functionality to match the
  // prototype's two-item subnav exactly (see handback DECISIONS).
  type AdminTab =
    | 'api-keys'
    | 'happy-code'
    | 'users'
    | 'email-access'
    | 'stats'
    | 'verticals'
    | 'custom-fields'
    | 'lead-routing'
    | 'tracked-domains'
    | 'companies';
  type AdminTabDef = { key: AdminTab; label: string; icon: typeof Users };
  const baseTabs: AdminTabDef[] = [
    { key: 'api-keys', label: 'Read-only API keys', icon: KeyRound },
    { key: 'happy-code', label: 'Feedback & SMS', icon: SlidersHorizontal },
    { key: 'users', label: 'Users & Access', icon: Users },
    { key: 'email-access', label: 'Email access', icon: Mail },
    { key: 'stats', label: 'Stats', icon: BarChart3 },
    { key: 'verticals', label: 'Verticals', icon: Layers },
    { key: 'custom-fields', label: 'Custom fields', icon: SlidersHorizontal },
    { key: 'lead-routing', label: 'Lead routing', icon: RouteIcon },
    { key: 'tracked-domains', label: 'Tracked domains', icon: Globe }
  ];
  // Hotels' main nav dropped Companies for Sites/Owners (see the Hotels nav
  // restructure spec) — this is its one remaining entry point, a deliberate
  // "data cleanup" surface for spotting duplicate brand/owner rows, so it's
  // tucked under Admin rather than gone. Every other vertical never lost
  // Companies from its main nav, so it never needs this tab.
  const isHotelsVertical = $derived(
    typeof session.selectedVertical === 'number' &&
      session.me?.verticals.find((v) => v.id === session.selectedVertical)?.slug === 'hotels'
  );
  const tabs = $derived(
    isHotelsVertical ? [...baseTabs, { key: 'companies', label: 'Companies', icon: Building2 } as AdminTabDef] : baseTabs
  );
  const TAB_KEYS = $derived(tabs.map((t) => t.key));

  // Subtab lives in the URL hash (#/admin/<subtab>) rather than local state,
  // so it's part of the same history stack as everything else.
  const tab = $derived(
    route.current.tab === 'admin' && TAB_KEYS.includes(route.current.sub as AdminTab)
      ? (route.current.sub as AdminTab)
      : 'users'
  );

  function selectTab(key: AdminTab) {
    route.push({ tab: 'admin', sub: key });
  }
</script>

<div class="flex h-full min-h-0 flex-col md:flex-row">
  <!-- subnav: horizontally-scrollable tab strip on mobile, 210px sidebar on desktop -->
  <div
    class="shrink-0 border-b border-toyama-cloudy-gray bg-white px-3 py-2 md:w-[210px] md:border-b-0 md:border-r md:px-3 md:py-[18px]"
  >
    <div class="hidden px-2.5 pb-2.5 text-[11px] font-semibold uppercase tracking-[.08em] text-dark-gray md:block">
      Settings
    </div>
    <div class="flex gap-1 overflow-x-auto md:flex-col md:overflow-visible">
      {#each tabs as t (t.key)}
        <button
          type="button"
          class="admin-nav-btn !w-auto whitespace-nowrap md:!w-full {tab === t.key ? 'active' : ''}"
          onclick={() => selectTab(t.key)}
          aria-current={tab === t.key ? 'page' : undefined}
        >
          <t.icon class="h-4 w-4 shrink-0" style="color:{tab === t.key ? '#226A73' : '#6B7072'}" />
          {t.label}
        </button>
      {/each}
    </div>
  </div>

  <div class="min-w-0 flex-1 overflow-y-auto">
    {#if tab === 'companies'}
      <CompaniesPage />
    {:else}
      <div class="tym-page">
        {#if tab === 'users'}
          <UsersPage />
        {:else if tab === 'happy-code'}
          <HappyCodeSettings />
        {:else if tab === 'api-keys'}
          <ApiKeysPage />
        {:else if tab === 'email-access'}
          <EmailAccessPage />
        {:else if tab === 'stats'}
          <StatsPage />
        {:else if tab === 'verticals'}
          <VerticalsPage />
        {:else if tab === 'custom-fields'}
          <CustomFieldsPage />
        {:else if tab === 'lead-routing'}
          <LeadRoutingPage />
        {:else if tab === 'tracked-domains'}
          <TrackedDomainsPage />
        {/if}
      </div>
    {/if}
  </div>
</div>
